// CheckoutAnalyticsDashboard, admin view of the full purchase funnel.
// Route: /admin-ap/checkout-analytics

import { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { Spinner } from '../../components/ui/Spinner';

interface CheckoutMetrics {
  totalPricingViews: number;
  totalCheckoutStarts: number;
  totalCompletedPayments: number;
  overallConversionRate: number;
  pricingToSelection: number;
  selectionToCheckout: number;
  checkoutToSubmit: number;
  submitToSuccess: number;
  totalRevenue: number;
  averageOrderValue: number;
  totalAbandonments: number;
  totalFailures: number;
  failureRate: number;
  topPlans:  Array<{ plan_name: string; conversions: number; revenue: number }>;
  topErrors: Array<{ error_code: string; count: number }>;
}

const STRIPE_ERRORS: Record<string, string> = {
  card_declined:      'Cartão recusado pelo banco',
  insufficient_funds: 'Saldo insuficiente',
  incorrect_cvc:      'CVV incorreto',
  expired_card:       'Cartão expirado',
  processing_error:   'Erro no processamento',
  fraudulent:         'Suspeita de fraude (Radar)',
  unknown:            'Motivo não informado',
};

type Timeframe = '7d' | '30d' | '90d';

const DAYS: Record<Timeframe, number> = { '7d': 7, '30d': 30, '90d': 90 };

function formatBRL(cents: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

function FunnelBar({ label, pct }: { label: string; pct: number }) {
  const color = pct >= 70 ? '#48BB78' : pct >= 50 ? '#C9A84C' : '#E53E3E';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>{label}</span>
        <span style={{ color, fontSize: 'var(--text-caption)', fontWeight: 700 }}>{pct.toFixed(1)}%</span>
      </div>
      <div style={{ height: '8px', backgroundColor: 'var(--bg-surface)', borderRadius: '999px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, backgroundColor: color, borderRadius: '999px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

export function CheckoutAnalyticsDashboard() {
  const [metrics,   setMetrics]   = useState<CheckoutMetrics | null>(null);
  const [timeframe, setTimeframe] = useState<Timeframe>('30d');
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);

  useEffect(() => { fetchMetrics(); }, [timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchMetrics() {
    setLoading(true);
    setError(null);
    const days  = DAYS[timeframe];
    const start = new Date(Date.now() - days * 86_400_000).toISOString();
    const end   = new Date().toISOString();

    const { data, error: rpcErr } = await supabase.rpc('get_checkout_metrics', {
      start_date: start,
      end_date:   end,
    });

    if (rpcErr) {
      setError(rpcErr.message);
    } else {
      setMetrics(data as CheckoutMetrics);
    }
    setLoading(false);
  }

  const kpis = metrics ? [
    {
      label: 'Conversão Geral',
      value: `${metrics.overallConversionRate.toFixed(1)}%`,
      sub:   `${metrics.totalCompletedPayments} de ${metrics.totalPricingViews} visitantes`,
      icon:  metrics.overallConversionRate >= 50 ? TrendingUp : TrendingDown,
      color: metrics.overallConversionRate >= 50 ? '#48BB78' : '#E53E3E',
    },
    {
      label: 'Receita Total',
      value: formatBRL(metrics.totalRevenue),
      sub:   `Ticket médio ${formatBRL(metrics.averageOrderValue)}`,
      icon:  DollarSign,
      color: 'var(--petrol)',
    },
    {
      label: 'Abandonos',
      value: String(metrics.totalAbandonments),
      sub:   `${metrics.totalCheckoutStarts > 0
        ? ((metrics.totalAbandonments / metrics.totalCheckoutStarts) * 100).toFixed(1)
        : '0'}% dos checkouts`,
      icon:  AlertTriangle,
      color: '#C9A84C',
    },
    {
      label: 'Taxa de Falha',
      value: `${metrics.failureRate.toFixed(1)}%`,
      sub:   `${metrics.totalFailures} tentativas falhas`,
      icon:  XCircle,
      color: '#E53E3E',
    },
  ] : [];

  // Identify biggest drop-off
  function dropOffMessage(m: CheckoutMetrics): string {
    const steps = [
      { pct: m.pricingToSelection,  label: 'Pricing → Seleção: usuários não estão escolhendo planos' },
      { pct: m.selectionToCheckout, label: 'Seleção → Checkout: fricção ao iniciar compra' },
      { pct: m.checkoutToSubmit,    label: 'Checkout → Submit: usuários desistindo no formulário' },
      { pct: m.submitToSuccess,     label: 'Submit → Sucesso: falhas de pagamento, verificar Stripe Radar' },
    ];
    const worst = steps.reduce((a, b) => (b.pct < a.pct ? b : a));
    return worst.label;
  }

  return (
    <div>
      <AdminPageHeader
        title="Analytics de Checkout"
        objective="Funil de conversão e oportunidades de otimização"
      />

      {/* Timeframe */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {(['7d', '30d', '90d'] as Timeframe[]).map(tf => (
          <button
            key={tf}
            onClick={() => setTimeframe(tf)}
            className={timeframe === tf ? 'btn btn-primary' : 'btn btn-ghost'}
            style={{ padding: '0.4rem 1rem', fontSize: 'var(--text-caption)' }}
          >
            {tf === '7d' ? '7 dias' : tf === '30d' ? '30 dias' : '90 dias'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Spinner />
        </div>
      ) : error ? (
        <div className="card" style={{ color: '#E53E3E', textAlign: 'center', padding: '2rem' }}>
          {error}
        </div>
      ) : metrics ? (
        <>
          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {kpis.map(({ label, value, sub, icon: Icon, color }) => (
              <div key={label} className="card" style={{ padding: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.375rem' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{label}</span>
                  <Icon size={18} color={color} />
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: 800, color, lineHeight: 1, marginBottom: '0.25rem' }}>
                  {value}
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: 0 }}>{sub}</p>
              </div>
            ))}
          </div>

          {/* Funnel */}
          <div className="card" style={{ padding: '1.5rem', marginBottom: '1.25rem' }}>
            <h2 style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-title)', margin: '0 0 1.25rem' }}>
              Funil de Conversão
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              <FunnelBar label="Pricing → Seleção de plano"    pct={metrics.pricingToSelection} />
              <FunnelBar label="Seleção → Início do checkout"  pct={metrics.selectionToCheckout} />
              <FunnelBar label="Checkout → Clique em pagar"    pct={metrics.checkoutToSubmit} />
              <FunnelBar label="Clique pagar → Sucesso"        pct={metrics.submitToSuccess} />
            </div>

            <div style={{
              display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
              backgroundColor: 'rgba(229,62,62,0.08)', border: '1px solid rgba(229,62,62,0.25)',
              borderRadius: 'var(--radius-card)', padding: '0.875rem 1rem',
            }}>
              <AlertTriangle size={16} color="#E53E3E" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ fontWeight: 700, color: '#E53E3E', fontSize: 'var(--text-caption)', margin: '0 0 0.2rem' }}>
                  Maior ponto de abandono
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>
                  {dropOffMessage(metrics)}
                </p>
              </div>
            </div>
          </div>

          {/* Top plans + Top errors */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

            <div className="card" style={{ padding: '1.25rem' }}>
              <h2 style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: '0 0 1rem' }}>
                Planos mais vendidos
              </h2>
              {metrics.topPlans.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic' }}>
                  Sem dados para o período.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {metrics.topPlans.map((plan, i) => (
                    <div key={plan.plan_name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                        <span style={{
                          width: '24px', height: '24px', borderRadius: '50%',
                          backgroundColor: 'var(--petrol)', color: 'var(--text-primary)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '11px', fontWeight: 700, flexShrink: 0,
                        }}>{i + 1}</span>
                        <div>
                          <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-caption)', margin: 0 }}>
                            {plan.plan_name}
                          </p>
                          <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: 0 }}>
                            {plan.conversions} conversões
                          </p>
                        </div>
                      </div>
                      <span style={{ fontWeight: 700, color: '#C9A84C', fontSize: 'var(--text-label)' }}>
                        {formatBRL(plan.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card" style={{ padding: '1.25rem' }}>
              <h2 style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: '0 0 1rem' }}>
                Erros mais comuns
              </h2>
              {metrics.topErrors.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic' }}>
                  Nenhuma falha registrada no período.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {metrics.topErrors.map(e => (
                    <div key={e.error_code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <p style={{ fontWeight: 600, color: '#E53E3E', fontSize: 'var(--text-caption)', margin: '0 0 1px' }}>
                          {e.error_code}
                        </p>
                        <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: 0 }}>
                          {STRIPE_ERRORS[e.error_code] ?? 'Erro desconhecido'}
                        </p>
                      </div>
                      <span style={{ fontWeight: 800, color: '#E53E3E', fontSize: 'var(--text-label)' }}>
                        {e.count}×
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </>
      ) : null}
    </div>
  );
}
